import React from "react";
import PropTypes from "prop-types";
import KVTable from "./forms/KVTable.react.js";
import KVInput from "./KVInput.react.js";
import AuthForm from "./forms/AuthForm.react.js";
import BodyForm from "./forms/BodyForm.react.js";
import ApiRequestLine from "./forms/ApiRequestLine.react.js";
import CaseCallTarget from "./forms/CaseCallTarget.react.js";
import DataSourceTarget from "./forms/DataSourceTarget.react.js";
import SingleInput from "./forms/SingleInput.react.js";
import SingleSelect from "./forms/SingleSelect.react.js";
import KvSimpleForm from "./forms/KvSimpleForm.react.js";

/**
 * 右侧抽屉（700px）。按 schema.components[*].type 分发到对应 form 组件。
 * 新增 component type = 加一个 case + 一个组件文件，不动其他逻辑。
 *
 * 核心:UI 形态完全由 component.type 决定,没有任何硬编码 action 名。
 */
function PropertyPanel({
  node,
  schema,
  isEdit,
  onComponentChange,
  onComponentValueChange,
  onComponentObjectChange,
  onComponentScalarChange,
  onComponentAdd,
  onComponentDelete,
  onDeleteNode,
  onClose,
}) {
  if (!node) return <div className="tfe-prop-panel" />;
  const actionSchema = schema?.actions?.[node.action];
  if (!actionSchema) return <div className="tfe-prop-panel" />;

  const renderComponent = (component) => {
    if (component.type === "single_input") {
      // 节点 valueType 优先；schema 声明次之；缺省 str
      const nodeValueType = node.valueType ?? component.valueType ?? "str";
      // schema 显式声明 value_type 时锁定 UI 类型下拉（与 Python is_value_type_locked 对齐）
      const valueTypeLocked = component.isValueTypeLocked === true;
      // key={node.id}：切换节点时 SingleInput 干净 remount，避免把上一个节点的
      // textarea rawText 错误地带到下一个节点（json / list 的字符 dump 问题修复）
      return (
        <SingleInput
          key={node.id}
          value={node.componentData}
          valueType={nodeValueType}
          valueTypeLocked={valueTypeLocked}
          placeholder={component.placeholder ?? ""}
          required={Boolean(component.required)}
          isEdit={isEdit}
          onChange={(v, vt) => onComponentScalarChange?.(node.id, v, vt)}
          onValueTypeChange={(vt) => onComponentScalarChange?.(node.id, node.componentData, vt)}
        />
      );
    }

    if (component.type === "single_select") {
      // single_select 与 single_input 共享「顶层标量 componentData」契约；
      // 区别：single_select 用 schema.options 限定可选值，UI 渲染 <select>，
      // 不暴露 value_type / 类型下拉。
      // key={node.id}：切换节点时干净 remount，避免上一个节点的选中态带到下一个节点。
      return (
        <SingleSelect
          key={node.id}
          value={node.componentData}
          options={component.options ?? []}
          placeholder={component.placeholder ?? ""}
          required={Boolean(component.required)}
          isEdit={isEdit}
          onChange={(v) => onComponentScalarChange?.(node.id, v, undefined)}
        />
      );
    }

    const rows = node.componentData?.[component.name] ?? [];

    if (component.type === "api_request_line") {
      const values = node.componentData?.[component.name] ?? {};
      return (
        <ApiRequestLine
          fields={component.fields ?? []}
          values={values}
          isEdit={isEdit}
          onChange={(k, v) => onComponentValueChange(node.id, component.name, k, v)}
        />
      );
    }
    if (component.type === "case_call_target") {
      const values = node.componentData?.[component.name] ?? {};
      return (
        <CaseCallTarget
          fields={component.fields ?? []}
          values={values}
          isEdit={isEdit}
          onChange={(k, v) => onComponentValueChange(node.id, component.name, k, v)}
        />
      );
    }
    if (component.type === "data_source_target") {
      const values = node.componentData?.[component.name] ?? {};
      return (
        <DataSourceTarget
          fields={component.fields ?? []}
          values={values}
          isEdit={isEdit}
          onChange={(k, v) => onComponentValueChange(node.id, component.name, k, v)}
        />
      );
    }

    if (component.type === "kv_input") {
      const values = node.componentData?.[component.name] ?? {};
      return (
        <KVInput
          fields={component.fields ?? []}
          values={values}
          onChange={(key, value) =>
            onComponentValueChange(node.id, component.name, key, value)
          }
          isEdit={isEdit}
        />
      );
    }

    if (component.type === "api_auth") {
      // 数据从 meta 切到 componentData[component.name](对象:{authType, authToken, ...})
      const auth = node.componentData?.[component.name] ?? {};
      const updateAuth = (field, val) =>
        onComponentValueChange(node.id, component.name, field, val);
      return (
        <AuthForm
          authType={auth.authType ?? "none"}
          authToken={auth.authToken ?? ""}
          authUser={auth.authUser ?? ""}
          authPwd={auth.authPwd ?? ""}
          isEdit={isEdit}
          onChange={updateAuth}
        />
      );
    }

    if (component.type === "api_body") {
      const bodyData = node.componentData?.[component.name] ?? {};
      const updateBody = (partial) =>
        onComponentObjectChange(node.id, component.name, { ...bodyData, ...partial });
      return (
        <BodyForm
          bodyType={bodyData.bodyType ?? "none"}
          rawFormat={bodyData.rawFormat ?? "JSON"}
          rawBody={bodyData.rawBody ?? ""}
          formData={bodyData.formData ?? []}
          urlEncoded={bodyData.urlEncoded ?? []}
          isEdit={isEdit}
          onMetaChange={(field, val) => updateBody({ [field]: val })}
          onFormDataChange={(i, f, v) => {
            const next = [...(bodyData.formData ?? [])];
            next[i] = { ...next[i], [f]: v };
            updateBody({ formData: next });
          }}
          onFormDataAdd={() => {
            const next = [...(bodyData.formData ?? []), { key: "", type: "String", val: "" }];
            updateBody({ formData: next });
          }}
          onFormDataDelete={(i) => {
            const next = [...(bodyData.formData ?? [])];
            next.splice(i, 1);
            updateBody({ formData: next });
          }}
          onUrlEncodedChange={(i, f, v) => {
            const next = [...(bodyData.urlEncoded ?? [])];
            next[i] = { ...next[i], [f]: v };
            updateBody({ urlEncoded: next });
          }}
          onUrlEncodedAdd={() => {
            const next = [...(bodyData.urlEncoded ?? []), { key: "", val: "" }];
            updateBody({ urlEncoded: next });
          }}
          onUrlEncodedDelete={(i) => {
            const next = [...(bodyData.urlEncoded ?? [])];
            next.splice(i, 1);
            updateBody({ urlEncoded: next });
          }}
        />
      );
    }

    if (component.type === "kv_simple") {
      return (
        <KvSimpleForm
          key={`${node.id}-${component.name}`}
          value={node.componentData?.[component.name]}
          valueLabel={component.valueLabel}
          isEdit={isEdit}
          onChange={(next) => onComponentObjectChange(node.id, component.name, next)}
        />
      );
    }

    if (component.type !== "kv" && component.type !== "kv_assert") {
      return null;
    }

    const type = component.type;
    const defaultType = type === "kv_assert" ? "==" : "String";

    return (
      <KVTable
        rows={Array.isArray(rows) ? rows : []}
        type={type}
        valueLabel={component.valueLabel}
        isEdit={isEdit}
        onChange={(i, f, v) => onComponentChange(node.id, component.name, i, f, v)}
        onAdd={() => onComponentAdd(node.id, component.name, defaultType)}
        onDelete={(i) => onComponentDelete(node.id, component.name, i)}
      />
    );
  };

  return (
    <div className="tfe-prop-panel open">
      <div className="tfe-prop-header">
        <div className="tfe-prop-header-title">
          <span
            style={{
              background: actionSchema.bg,
              padding: "4px 8px",
              borderRadius: "6px",
              color: actionSchema.color,
            }}
          >
            {actionSchema.icon}
          </span>
          {actionSchema.label}
          {["single_input", "single_select"].includes(
            actionSchema.components?.[0]?.type
          ) &&
            actionSchema.components[0].required && (
              <span
                className="tfe-section-required"
                title="必填"
                aria-label="必填"
              >
                *
              </span>
            )}
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          {isEdit && (
            <button
              type="button"
              className="tfe-btn tfe-btn-outline"
              style={{ borderColor: "#e2e8f0", color: "#ef4444", height: "32px" }}
              onClick={() => onDeleteNode(node.id)}
            >
              删除节点
            </button>
          )}
          <button
            type="button"
            className="tfe-btn tfe-btn-outline"
            style={{
              padding: "0 12px",
              height: "32px",
              borderColor: "#e2e8f0",
              color: "#64748b",
            }}
            onClick={onClose}
          >
            ✖
          </button>
        </div>
      </div>
      <div className="tfe-sections-container">
        {actionSchema.components.map((component, idx) => {
          if (component.type === "no_input") return null;
          const blockKey = component.name || `${component.type}-${idx}`;
          const hideSectionTitle =
            component.type === "single_input" || component.type === "single_select";
          return (
            <div className="tfe-section-block" key={blockKey}>
              {!hideSectionTitle && (
                <div className="tfe-section-title-text">
                  {component.header}
                  {component.required && (
                    <span
                      className="tfe-section-required"
                      title="必填"
                      aria-label="必填"
                    >
                      *
                    </span>
                  )}
                </div>
              )}
              {renderComponent(component)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

PropertyPanel.propTypes = {
  node: PropTypes.object,
  schema: PropTypes.object.isRequired,
  isEdit: PropTypes.bool,
  onComponentChange: PropTypes.func.isRequired,
  onComponentValueChange: PropTypes.func.isRequired,
  onComponentObjectChange: PropTypes.func.isRequired,
  onComponentScalarChange: PropTypes.func,
  onComponentAdd: PropTypes.func.isRequired,
  onComponentDelete: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PropertyPanel;
